import { z } from "zod";
const identifierSchema = z.string().trim().min(1).max(500);
export const WORKSPACE_NOTIFICATION_REASON_PRIORITY = [
    "approval_pending",
    "task_attention",
    "automation_attention",
    "connector_attention",
    "billing_notice",
    "mention",
    "share_received",
    "record_assignment",
    "mailbox_assignment",
    "session_result",
    "discussion_reply",
    "reaction",
    "inbox_attention",
    "proposal_review",
    "attention_rule",
    "channel_access_granted",
    "share_moved",
    "task_completed",
    "automation_completed",
    "watched_resource_activity",
];
export const workspaceNotificationReasonSchema = z.enum(WORKSPACE_NOTIFICATION_REASON_PRIORITY);
export const WORKSPACE_NOTIFICATION_SETTING_REASONS = WORKSPACE_NOTIFICATION_REASON_PRIORITY.filter((reason) => reason !== "billing_notice" && reason !== "share_moved" && reason !== "mailbox_assignment"
    && reason !== "session_result");
/** Reasons that reach a person only in the app, whatever their settings say. */
export const WORKSPACE_NOTIFICATION_IN_APP_ONLY_REASONS = [
    "reaction",
    "share_moved",
    "mailbox_assignment",
    "session_result",
];
/**
 * Reasons that report something that happened rather than ask a person to
 * act. A notification with only these reasons is left to the Feed; the Inbox
 * keeps one when any of its reasons asks for action.
 */
export const WORKSPACE_NOTIFICATION_INFORMATIONAL_REASONS = [
    "reaction",
    "share_moved",
    "task_completed",
    "automation_completed",
    "watched_resource_activity",
];
export const workspaceNotificationAttentionStateSchema = z.enum([
    "needs_attention",
    "handled",
    "dismissed",
]);
export const workspaceNotificationSourceTypeSchema = z.enum([
    "channel",
    "channel_post",
    "thread",
    "record",
    "session",
    "approval",
    "automation",
    "task",
    "connector_job",
    "plugin_connection",
    "inbox_item",
    "twin_batch",
    "twin_attention",
    "billing_notice",
    /** A share (docs/specs/twin-shares.md): its rows hold no text and reach only its participants. */
    "share",
    /** A shared mailbox's conversation (section 3): its rows hold no text and reach only the mailbox's readers. */
    "mailbox_conversation",
]);
export const workspaceNotificationActorSchema = z.object({
    type: z.enum(["user", "agent", "service"]),
    id: z.string().trim().min(1).max(200),
}).strict();
export const workspaceNotificationSourceSchema = z.object({
    type: workspaceNotificationSourceTypeSchema,
    id: identifierSchema,
    channelId: identifierSchema.nullable(),
    threadId: identifierSchema.nullable(),
}).strict();
export const workspaceNotificationItemSchema = z.object({
    id: identifierSchema,
    key: z.string().regex(/^workspace-notification:[^:]+$/u),
    occurredAt: z.number().int().nonnegative(),
    actor: workspaceNotificationActorSchema,
    source: workspaceNotificationSourceSchema,
    reasons: z.array(workspaceNotificationReasonSchema).min(1),
    primaryReason: workspaceNotificationReasonSchema,
    presentation: z.record(z.string(), z.unknown()),
    actionContext: z.record(z.string(), z.unknown()),
    attentionState: workspaceNotificationAttentionStateSchema,
    readAt: z.number().int().nonnegative().nullable(),
}).strict().superRefine((item, context) => {
    if (item.key !== `workspace-notification:${item.id}`) {
        context.addIssue({
            code: "custom",
            path: ["key"],
            message: "Notification key must identify the item",
        });
    }
    if (new Set(item.reasons).size !== item.reasons.length) {
        context.addIssue({
            code: "custom",
            path: ["reasons"],
            message: "Notification reasons must be unique",
        });
    }
    const primaryReason = WORKSPACE_NOTIFICATION_REASON_PRIORITY.find((reason) => item.reasons.includes(reason));
    if (primaryReason !== item.primaryReason) {
        context.addIssue({
            code: "custom",
            path: ["primaryReason"],
            message: "Primary reason must be the highest-priority reason",
        });
    }
});
export const listWorkspaceNotificationsQuerySchema = z.object({
    cursor: z.string().trim().min(1).max(1_024).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    attentionState: z.enum([
        "needs_attention",
        "handled",
        "dismissed",
        "closed",
        "all",
    ]).default("needs_attention"),
    readState: z.enum(["read", "unread", "all"]).default("all"),
    /** "action" keeps only notifications that ask a person to act; counts follow. */
    reasons: z.enum(["all", "action"]).default("all"),
}).strict();
export const workspaceNotificationsResponseSchema = z.object({
    items: z.array(workspaceNotificationItemSchema),
    openCount: z.number().int().nonnegative(),
    unreadCount: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
}).strict();
export const workspaceNotificationPatchRequestSchema = z.object({
    read: z.boolean().optional(),
    attentionState: workspaceNotificationAttentionStateSchema.optional(),
}).strict().refine((value) => value.read !== undefined || value.attentionState !== undefined, { message: "At least one notification state change is required" });
export const workspaceNotificationResponseSchema = z.object({
    notification: workspaceNotificationItemSchema,
}).strict();
export const workspaceNotificationClientSchema = z.enum([
    "in_app",
    "slack",
    "email",
    "teams",
]);
export const workspaceNotificationExternalClientSchema = z.enum([
    "slack",
    "email",
    "teams",
]);
export const workspaceNotificationDeliveryModeSchema = z.enum([
    "off",
    "immediate",
    "daily_digest",
]);
const ianaTimeZoneSchema = z.string().trim().min(1).max(100).refine((value) => {
    try {
        new Intl.DateTimeFormat("en", { timeZone: value }).format(0);
        return true;
    }
    catch {
        return false;
    }
}, "Expected an IANA timezone");
export const workspaceNotificationPreferenceSchema = z.object({
    client: workspaceNotificationClientSchema,
    reason: workspaceNotificationReasonSchema,
    mode: workspaceNotificationDeliveryModeSchema,
    timeZone: ianaTimeZoneSchema.nullable(),
    digestLocalTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u).nullable(),
}).strict().superRefine((preference, context) => {
    if (WORKSPACE_NOTIFICATION_IN_APP_ONLY_REASONS.includes(preference.reason)
        && preference.client !== "in_app" && preference.mode !== "off") {
        context.addIssue({ code: "custom", path: ["client"],
            message: preference.reason === "reaction" ? "Reaction notifications are in-app only" : "These notifications are in-app only" });
    }
    if (preference.client === "in_app" && preference.mode === "daily_digest") {
        context.addIssue({
            code: "custom",
            path: ["mode"],
            message: "In-app notifications do not support digest delivery",
        });
    }
    const hasSchedule = preference.timeZone !== null && preference.digestLocalTime !== null;
    if ((preference.mode === "daily_digest") !== hasSchedule) {
        context.addIssue({
            code: "custom",
            path: ["timeZone"],
            message: "A digest schedule is required only for daily digest delivery",
        });
    }
});
export const workspaceNotificationPreferencesResponseSchema = z.object({
    preferences: z.array(workspaceNotificationPreferenceSchema),
}).strict();
export const workspaceNotificationPreferencePatchRequestSchema = workspaceNotificationPreferenceSchema;
export const workspaceNotificationPreferenceResponseSchema = z.object({
    preference: workspaceNotificationPreferenceSchema,
}).strict();
export const workspaceNotificationEndpointStateSchema = z.enum([
    "unavailable",
    "disconnected",
    "connected",
    "reconnect_required",
]);
export const workspaceNotificationClientCapabilitySchema = z.object({
    client: workspaceNotificationClientSchema,
    available: z.boolean(),
    connectionState: workspaceNotificationEndpointStateSchema,
}).strict();
export const workspaceNotificationCapabilitiesResponseSchema = z.object({
    digestLocalTime: z.literal("09:00"),
    changesApplyToFutureItems: z.literal(true),
    clients: z.array(workspaceNotificationClientCapabilitySchema).length(4),
}).strict();
export const workspaceNotificationWatchResourceTypeSchema = z.enum([
    "record",
    "thread",
]);
export const workspaceNotificationWatchSchema = z.object({
    id: identifierSchema,
    resourceType: workspaceNotificationWatchResourceTypeSchema,
    resourceId: identifierSchema,
    channelId: identifierSchema,
    createdAt: z.number().int().nonnegative(),
}).strict();
export const workspaceNotificationWatchCreateRequestSchema = z.object({
    resourceType: workspaceNotificationWatchResourceTypeSchema,
    resourceId: identifierSchema,
    channelId: identifierSchema,
}).strict();
export const workspaceNotificationWatchDeleteRequestSchema = z.object({
    resourceType: workspaceNotificationWatchResourceTypeSchema,
    resourceId: identifierSchema,
}).strict();
export const workspaceNotificationWatchListRequestSchema = z.object({
    resourceType: workspaceNotificationWatchResourceTypeSchema.optional(),
    resourceId: identifierSchema.optional(),
}).strict().refine((value) => Boolean(value.resourceType) === Boolean(value.resourceId), { message: "resourceType and resourceId must be provided together" });
export const workspaceNotificationWatchesResponseSchema = z.object({
    watches: z.array(workspaceNotificationWatchSchema),
}).strict();
export const workspaceNotificationWatchResponseSchema = z.object({
    watch: workspaceNotificationWatchSchema,
}).strict();
export const workspaceNotificationMutationOkResponseSchema = z.object({
    ok: z.literal(true),
}).strict();
const workerNotificationSettingsEnvelopeSchema = z.object({
    runId: identifierSchema,
    tenantSlug: z.string().trim().min(1),
});
/*
 * Worker-secret envelope a running session uses to manage the acting person's
 * own notification settings. Identity comes from the run, never the payload,
 * so a Twin reached from Slack, Teams or the CLI edits the same rows the app,
 * SDK and MCP surfaces edit, under the same rules.
 */
export const workerNotificationSettingsCommandRequestSchema = z.discriminatedUnion("command", [
    workerNotificationSettingsEnvelopeSchema.extend({
        command: z.literal("capabilities.get"),
    }).strict(),
    workerNotificationSettingsEnvelopeSchema.extend({
        command: z.literal("preferences.list"),
    }).strict(),
    workerNotificationSettingsEnvelopeSchema.extend({
        command: z.literal("preference.update"),
        input: workspaceNotificationPreferencePatchRequestSchema,
    }).strict(),
    workerNotificationSettingsEnvelopeSchema.extend({
        command: z.literal("watches.list"),
        input: workspaceNotificationWatchListRequestSchema.optional(),
    }).strict(),
    workerNotificationSettingsEnvelopeSchema.extend({
        command: z.literal("watch.create"),
        input: workspaceNotificationWatchCreateRequestSchema,
    }).strict(),
    workerNotificationSettingsEnvelopeSchema.extend({
        command: z.literal("watch.delete"),
        input: workspaceNotificationWatchDeleteRequestSchema,
    }).strict(),
]);
export const notificationPathParamsSchema = z.object({ notificationId: identifierSchema.optional() }).strict();
